import { useCallback, useEffect } from 'react'

import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Stack } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'
import { TicketsConnectionDocument } from 'generated/resources/graphql'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { Features } from 'generated/featureDefinitions'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import Loading from 'modules/core/components/Loading'
import { useActivities } from 'modules/core/lib/activities/provider'

import {
  TicketList,
  type TicketListFetchPolicy,
  type TicketListOnData,
  type TicketListPresetId,
} from 'modules/ticketing/components/TicketList/TicketList'
import { type TicketNode } from 'modules/ticketing/components/TicketList/ticketListConfig'
import { trackGoToTicketing } from 'modules/ticketing/lib/tracking'
import { useTicketingOnboardingState } from 'modules/ticketing/hooks/useTicketingOnboardingState'
import { TicketingOnboarding } from 'modules/ticketing/components/Onboarding/TicketingOnboarding'
import { NotConfiguredEmptyState } from 'modules/ticketing/components/Onboarding/NotConfiguredEmptyState'
import { useOneMutationSubscription } from 'modules/ai/lib/useOneMutationSubscription'

interface TicketListPageProps {
  availablePresets?: ReadonlyArray<TicketListPresetId>
  defaultPreset?: TicketListPresetId
  onData?: TicketListOnData
  fetchPolicy?: TicketListFetchPolicy
}

export const TicketListPageContent = ({
  availablePresets,
  defaultPreset,
  onData,
  fetchPolicy,
}: TicketListPageProps) => {
  const { openActivity } = useActivities()

  useOneMutationSubscription('CreateTicketingTicket', TicketsConnectionDocument)

  const { has_dev_ai_ticketing_core, loadingFeatures } = useFeatures([
    Features.dev_ai_ticketing_core,
  ])

  const onboardingState = useTicketingOnboardingState({
    skip: loadingFeatures || !has_dev_ai_ticketing_core,
  })

  useEffect(() => {
    trackGoToTicketing()
  }, [])

  const openTicketDetail = useCallback(
    (item: TicketNode) => {
      openActivity('ticketing.viewTicket', { ticketId: String(item.id) })
    },
    [openActivity]
  )

  const handleOpenCreateDialog = useCallback(() => {
    openActivity('ticketing.createTicket')
  }, [openActivity])

  const isLoading = loadingFeatures || onboardingState.status === 'loading'

  if (isLoading) {
    return <Loading />
  }

  if (!has_dev_ai_ticketing_core) {
    return (
      <Stack gap="4" paddingY="p-4">
        <OneEmptyState
          title={i18n.t('ticketing.inbox.no_access_title')}
          description={i18n.t('ticketing.inbox.no_access_description')}
        />
      </Stack>
    )
  }

  if (onboardingState.status === 'not_configured') {
    return <NotConfiguredEmptyState />
  }

  if (onboardingState.status === 'onboarding') {
    return <TicketingOnboarding />
  }

  return (
    <Stack gap="4" paddingY="p-4">
      <TicketList
        availablePresets={availablePresets}
        defaultPreset={defaultPreset}
        onData={onData}
        fetchPolicy={fetchPolicy}
        onOpenCreateDialog={handleOpenCreateDialog}
        onItemClick={openTicketDetail}
      />
    </Stack>
  )
}

const TicketListPage = (props: TicketListPageProps) => {
  return (
    <ResponsivePage.Body variant="fullscreen" height="full">
      <TicketListPageContent {...props} />
    </ResponsivePage.Body>
  )
}

export default TicketListPage
